/**
 * /market/:slug/reservar — reservas de mesa públicas con branding del tenant.
 */
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketReservarPage } from "@/components/market/MarketReservarPage";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }) => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap",
};

export const Route = createFileRoute("/market_/$slug/reservar")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: ReservarRoute,
  head: ({ params, loaderData }) => {
    const result = loaderData as MarketCatalogResult | undefined;
    const name = result?.ok ? result.data.tenant.name : params.slug;
    return {
      meta: [
        { title: `Reservar — ${name}` },
        { name: "description", content: `Reservá mesa en ${name}` },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover",
        },
      ],
      links: [{ rel: "manifest", href: `/api/manifest/${params.slug}` }, FONT_LINK],
    };
  },
});

function ReservarRoute() {
  const { slug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offline = renderMarketPublicOffline(result);
  if (offline) return offline;
  if (!result.ok) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[#0a1620] p-6 text-center text-white">
        <p className="text-sm text-white/70">Reservas no disponibles.</p>
      </div>
    );
  }

  return <MarketReservarPage slug={slug} data={result.data} />;
}
